import { PRESENTATION_NAVIGATOR_TOUR_SEEN_STORAGE_KEY } from '@ardoq/global-consts';
import { type APICurrentUser, PersonalSetting } from '@ardoq/api-types';

const NAVIGATOR_DISMISSED_STORAGE_KEY = 'presentationNavigatorDismissed';

const readStoredFlag = (key: string, fallback: boolean): boolean => {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return fallback;
  } catch {
    return fallback;
  }
};

const writeStoredFlag = (key: string, value: boolean): void => {
  try {
    window.localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    // Ignore storage failures (e.g. blocked in sandboxed iframes)
  }
};

export const getStoredIsNavigatorDismissed = (): boolean =>
  readStoredFlag(NAVIGATOR_DISMISSED_STORAGE_KEY, false);

export const setStoredIsNavigatorDismissed = (isDismissed: boolean): void =>
  writeStoredFlag(NAVIGATOR_DISMISSED_STORAGE_KEY, isDismissed);

export const getStoredHasSeenNavigatorTour = (): boolean =>
  readStoredFlag(PRESENTATION_NAVIGATOR_TOUR_SEEN_STORAGE_KEY, false);

export const setStoredHasSeenNavigatorTour = (hasSeen: boolean): void =>
  writeStoredFlag(PRESENTATION_NAVIGATOR_TOUR_SEEN_STORAGE_KEY, hasSeen);

type HasSeenNavigatorTourArgs = {
  currentUser: APICurrentUser | null;
  hasSeenTourInThisBrowser: boolean;
};

/**
 * A signed-in viewer carries the tour with them, to every browser and every
 * organization they open a presentation in. An anonymous viewer of a public
 * presentation has no user to carry it, so their local flag is all there is.
 */
export const hasSeenNavigatorTour = ({
  currentUser,
  hasSeenTourInThisBrowser,
}: HasSeenNavigatorTourArgs): boolean =>
  Boolean(
    currentUser?.settings[PersonalSetting.HAS_SEEN_PRESENTATION_NAVIGATOR_TOUR]
  ) || hasSeenTourInThisBrowser;
